import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Users, 
  Target, 
  TrendingUp, 
  CheckCircle, 
  Star,
  FileText,
  Search,
  Filter,
  Plus,
  ArrowRight,
  Clock,
  DollarSign,
  Globe,
  Zap,
  Shield,
  Award,
  BarChart3,
  MessageSquare,
  Calendar,
  MapPin,
  Briefcase,
  Mail,
  Phone,
  Play,
  Download
} from 'lucide-react';
import CommonContactUs from './CommonContactUs';

const ForEmployersPage = () => {
  const [activeTab, setActiveTab] = useState('services');

  const services = [
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: "Talent Acquisition",
      description: "Find the best tech talent for your organization with our comprehensive recruitment solutions.",
      features: ["AI-powered candidate matching", "Industry-specific sourcing", "Background verification", "Interview coordination"],
      link: "/talent-acquisition"
    },
    {
      icon: <Target className="w-8 h-8 text-green-600" />,
      title: "HR Consulting",
      description: "Strategic human resources consulting to optimize your talent acquisition and retention strategies.",
      features: ["HR strategy development", "Policy optimization", "Compliance guidance", "Performance management"],
      link: "/hr-consulting"
    },
    // {
    //   icon: <Clock className="w-8 h-8 text-purple-600" />,
    //   title: "Contract Staffing",
    //   description: "Flexible staffing solutions for project-based work and temporary positions.",
    //   features: ["Quick deployment", "Flexible contracts", "Quality assurance", "Cost-effective solutions"],
    //   link: "/contract-staffing"
    // },
    {
      icon: <TrendingUp className="w-8 h-8 text-orange-600" />,
      title: "Tech Training",
      description: "Upskill your team with cutting-edge technical training programs delivered by industry experts.",
      features: ["Customized training programs", "Certification courses", "Hands-on workshops", "Progress tracking"],
      link: "/tech-training"
    },
    {
      icon: <Star className="w-8 h-8 text-red-600" />,
      title: "Employer Branding",
      description: "Build a strong employer brand that attracts top talent and showcases your company culture.",
      features: ["Brand strategy development", "Content creation", "Social media management", "Employee advocacy"],
      link: "/employer-branding"
    },
    // {
    //   icon: <Search className="w-8 h-8 text-indigo-600" />,
    //   title: "Job Matching",
    //   description: "Advanced job matching technology to connect you with the right candidates.",
    //   features: ["AI-powered matching", "Skills assessment", "Cultural fit analysis", "Predictive analytics"],
    //   link: "/job-matching"
    // }
  ];

  const benefits = [
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "Fast Hiring",
      description: "Reduce time-to-hire by up to 60% with our streamlined recruitment process."
    },
    {
      icon: <Shield className="w-8 h-8 text-green-600" />,
      title: "Quality Assurance",
      description: "Rigorous screening and verification processes ensure you get the best candidates."
    },
    {
      icon: <DollarSign className="w-8 h-8 text-purple-600" />,
      title: "Cost Effective",
      description: "Reduce recruitment costs while improving the quality of your hires."
    },
    {
      icon: <Globe className="w-8 h-8 text-orange-600" />,
      title: "Global Reach",
      description: "Access talent from around the world with our extensive network."
    }
  ];

  const testimonials = [
    {
      name: "Jennifer Martinez",
      role: "HR Director",
      company: "InnovateTech Solutions",
      content: "TechMind helped us reduce our hiring time by 50% and improved our candidate quality significantly.",
      rating: 5
    },
    {
      name: "David Chen",
      role: "CEO",
      company: "StartupXYZ",
      content: "Their contract staffing service was perfect for our project-based needs. Highly recommended!",
      rating: 5
    },
    {
      name: "Sarah Williams",
      role: "Talent Acquisition Manager",
      company: "Global Corp",
      content: "The AI-powered job matching technology is game-changing. We found exceptional candidates quickly.",
      rating: 5
    }
  ];

  const resources = [
    {
      title: "Hiring Best Practices Guide",
      description: "Learn the latest strategies for effective hiring and talent acquisition.",
      icon: <FileText className="w-6 h-6" />,
      link: "#"
    },
    {
      title: "Employer Branding Toolkit",
      description: "Tools and templates to build a strong employer brand.",
      icon: <Star className="w-6 h-6" />,
      link: "#"
    },
    {
      title: "Interview Questions Database",
      description: "Curated interview questions for different roles and industries.",
      icon: <MessageSquare className="w-6 h-6" />,
      link: "#"
    },
    {
      title: "HR Compliance Checklist",
      description: "Stay compliant with employment laws and regulations.",
      icon: <Shield className="w-6 h-6" />,
      link: "#"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#1A193B] pt-20 text-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {/* Find Your Perfect Talent */}
              For Employers
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-green-100">
              {/* Comprehensive recruitment solutions to help you build exceptional teams */}
              Find, hire and retain the best tech talent for your organization_
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/submit-job"
                className="px-8 py-3 bg-white text-green-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Post a Job
              </Link>
              <Link 
                to="/contact"
                className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-green-600 transition-colors font-medium"
              >
                Get Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose TechMind for Your Hiring Needs?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide end-to-end recruitment solutions that help you find, hire, and retain the best talent
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {/* Our Recruitment Services */}
              Services for Employers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {/* Comprehensive solutions tailored to meet your specific hiring needs */}
              TechMind offers comprehensive talent solutions designed specifically for employers in the tech industry. Our services help you find, hire, and retain the best talent to drive your organization forward.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border p-8 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-6">
                  {service.icon}
                  <h3 className="text-2xl font-semibold text-gray-900 ml-3">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>
                {/* <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul> */}
                <Link 
                  to={service.link}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {/* Our Recruitment Process */}
              Why Choose TechMind for Your Talent Needs
            </h2>
            {/* <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A streamlined approach to finding and hiring the perfect candidates
            </p> */}
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Industry Expertise",
                description: "Our specialized recruiters understand the nuances of the tech industry, ensuring better matches."
              },
              {
                step: "02",
                title: "Advanced Matching",
                description: "Our proprietary algorithms and human expertise combine to match candidates with opportunities."
              },
              {
                step: "03",
                title: "Global Network",
                description: "With a vast network of tech professionals worldwide, we connect talent across borders."
              },
              {
                step: "04",
                title: "End-to-End Support",
                description: "We provide comprehensive support throughout the entire recruitment process."
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {process.title}
                </h3>
                <p className="text-gray-600">
                  {process.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      {/* <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Employer Resources
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Access our library of resources to improve your hiring and HR processes
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {resources.map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="bg-white rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-4 text-blue-600">
                  {resource.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {resource.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {resource.description}
                </p>
                <Link 
                  to={resource.link}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                >
                  Download
                  <Download className="w-4 h-4 ml-1" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Testimonials Section */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Success stories from companies that have transformed their hiring with TechMind
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.1 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">
                  "{testimonial.content}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role} at {testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-16 bg-[#1A193B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Transform Your Hiring?
            </h2>
            <p className="text-xl mb-8 text-green-100">
              Join hundreds of companies that trust TechMind for their recruitment needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-3 bg-white text-green-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Get Started
              </Link>
              <Link 
                to="/submit-job"
                className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-green-600 transition-colors font-medium"
              >
                Post a Job
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs/>
      </section>
    </div>
  );
};

export default ForEmployersPage; 